import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Camera, 
  Upload, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  ArrowLeft,
  Image as ImageIcon,
  FolderOpen
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUploadPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, [token]);

  const fetchCategories = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/product-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Vælg venligst en billedfil');
      return;
    }
    
    // Max 10MB
    if (file.size > 10 * 1024 * 1024) {
      alert('Billedet er for stort (max 10MB)');
      return;
    }
    
    setSelectedFile(file);
    setUploadResult(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCategory) {
      alert('Vælg venligst både et billede og en produktkategori');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Category is sent as query parameter, not form field
      const response = await axios.post(
        `${API}/product-images/upload?category=${encodeURIComponent(selectedCategory)}`, 
        formData, 
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUploadResult({
        success: true,
        filename: response.data.filename,
        message: response.data.message || 'Billede uploadet!'
      });
      
      // Clear file preview but keep category for next upload
      setSelectedFile(null);
      setPreviewUrl(null);
      // Don't clear category - user likely wants to upload more of same type
      
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadResult({
        success: false,
        message: err.response?.data?.detail || 'Upload fejlede'
      });
    } finally {
      setUploading(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-4 py-4">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white hover:bg-slate-800 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-outfit">Upload Billede</h1>
            <p className="text-slate-400 text-sm">Tag eller vælg et produktbillede</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Category Selection */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-700 flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Produktkategori
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select 
              value={selectedCategory} 
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger 
                className="w-full" 
                data-testid="category-select"
              >
                <SelectValue placeholder="Vælg kategori..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Image Preview or Upload Area */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-700 flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Billede
            </CardTitle>
          </CardHeader>
          <CardContent>
            {previewUrl ? (
              <div className="space-y-3">
                <div className="relative rounded-lg overflow-hidden bg-slate-100">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="w-full h-64 object-contain"
                  />
                  <Button
                    onClick={clearSelection}
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    data-testid="clear-image-btn"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-slate-500 text-center">
                  {selectedFile?.name}
                </p>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm mb-4">
                  Tag et billede eller vælg fra galleri
                </p>
                
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={() => cameraInputRef.current?.click()}
                    variant="outline"
                    className="flex-1 max-w-32"
                    data-testid="camera-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Kamera
                  </Button>
                  
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="flex-1 max-w-32"
                    data-testid="gallery-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Galleri
                  </Button>
                </div>
              </div>
            )}
            
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Upload Result */}
        {uploadResult && (
          <Card className={`border-0 ${uploadResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                {uploadResult.success ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {uploadResult.success ? 'Upload gennemført!' : 'Upload fejlede'}
                  </p>
                  <p className={`text-sm ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
                    {uploadResult.filename || uploadResult.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload Button */}
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || !selectedCategory || uploading}
          className="w-full h-14 bg-teal-500 hover:bg-teal-600 text-white font-semibold text-lg disabled:opacity-50"
          data-testid="upload-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Uploader...
            </>
          ) : (
            <>
              <Upload className="h-5 w-5 mr-2" />
              Upload billede
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ImageUploadPage;
